import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { ObjectDetailsSpacer } from '@atlassian/jira-servicedesk-insight-attribute-views/src/common/ui/styled.tsx';
import { TextAreaReadView } from '@atlassian/jira-servicedesk-insight-attribute-views/src/ui/text-area/index.tsx';
import {
	useDefaultAttributeValue,
	getDefaultAttributeValue,
} from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/default/index.tsx';
import { useEditableAttribute } from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/use-editable-attribute/index.tsx';
import type { DefaultAttributeType } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-attribute-type.tsx';
import { textAreaAttributeProps } from '@atlassian/jira-servicedesk-insight-shared-ui/src/common/constants/index.tsx';
import { InlineEditableAttribute } from '../../common/ui/editable-attribute/index.tsx';

export type Props = {
	isEditable: boolean;
	attributeType: DefaultAttributeType;
};

export const TextAreaAttribute = (props: Props) => {
	const { attributeType, isEditable } = props;

	const [{ attribute }] = useEditableAttribute(attributeType);
	const [value, setValue] = useDefaultAttributeValue(attribute);

	const onChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setValue(event.target.value);
		},
		[setValue],
	);

	const resetValue = useCallback(() => {
		setValue(getDefaultAttributeValue(attribute));
	}, [attribute, setValue]);

	return (
		<InlineEditableAttribute
			testId="servicedesk-insight-inline-editable-attributes.ui.text-area-attribute"
			value={value}
			onChange={onChange}
			storedValue={getDefaultAttributeValue(attribute)}
			isEditable={isEditable}
			attributeType={attributeType}
			hideActionButtons={false}
			renderReadView={() => (
				<ObjectDetailsSpacer>
					<TextAreaReadView value={value} />
				</ObjectDetailsSpacer>
			)}
			renderEditView={({ hasValidationError, ...fieldProps }) => (
				<EditViewContainer>
					<TextArea
						{...fieldProps}
						{...textAreaAttributeProps}
						autoFocus
						isInvalid={hasValidationError}
					/>
				</EditViewContainer>
			)}
			onResetValue={resetValue}
		/>
	);
};

export const TextAreaEditableAttribute = (props: Props) => (
	<JSErrorBoundary
		id="InsightInlineEditableTextAreaAttribute"
		packageName="jiraServicedeskInsightEditableTextAreaAttribute"
		teamName="falcons"
		fallback="unmount"
	>
		<TextAreaAttribute {...props} />
	</JSErrorBoundary>
);

// eslint-disable-next-line @atlaskit/design-system/use-primitives, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EditViewContainer = styled.div({
	// Used to offset InlineEdit component content styles for seamless transition
	paddingTop: token('space.100'),
	paddingRight: token('space.075'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.075'),
});
